import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { environment } from '@/environments/environment';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
    if (request.headers.has('Authorization')) {
        return next(request);
    }

    const apiBase = environment.apiBaseUrl;
    const absoluteApiBase = `${window.location.origin}${apiBase}`;
    const isApiRequest = request.url.startsWith(apiBase) || request.url.startsWith(absoluteApiBase);

    const authService = inject(AuthService);
    const router = inject(Router);
    const refreshPath = environment.auth.refreshPath;
    const logoutPath = environment.auth.logoutPath;
    const isRefreshRequest = request.url.includes(refreshPath);
    const isLogoutRequest = request.url.includes(logoutPath);
    const token = authService.getAccessToken();

    if (!isApiRequest) {
        return next(request);
    }

    if (!token) {
        return next(request).pipe(
            catchError((error) => {
                if (error.status === 401 && error?.error?.code === 'USER_INACTIVE') {
                    authService.clearAccessToken();
                    authService.clearCurrentUser();
                    router.navigateByUrl('/error/401');
                    return throwError(() => error);
                }

                if (error.status !== 401 || isRefreshRequest || isLogoutRequest) {
                    return throwError(() => error);
                }

                authService.login();
                return throwError(() => error);
            })
        );
    }

    const authorizedRequest = request.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`
        }
    });

    return next(
        authorizedRequest
    ).pipe(
        catchError((error) => {
            if (error.status === 401 && error?.error?.code === 'USER_INACTIVE') {
                authService.clearAccessToken();
                authService.clearCurrentUser();
                router.navigateByUrl('/error/401');
                return throwError(() => error);
            }

            if (error.status !== 401 || isRefreshRequest || isLogoutRequest || request.headers.has('X-Refresh-Retry')) {
                return throwError(() => error);
            }

            return from(authService.refreshToken()).pipe(
                switchMap((newToken) => {
                    if (!newToken) {
                        authService.login();
                        return throwError(() => error);
                    }

                    const retriedRequest = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${newToken}`,
                            'X-Refresh-Retry': '1'
                        }
                    });

                    return next(retriedRequest);
                }),
                catchError((refreshError) => {
                    authService.logout();
                    return throwError(() => refreshError);
                })
            );
        })
    );
};
